import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BatchHistoryPanel = ({ currentBatch, onBatchSelect }) => {
  const [batchHistory, setBatchHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading batch history
    setTimeout(() => {
      const mockHistory = [
        {
          id: 'BT-2024-001247',
          batchNumber: 'BT-2024-001247',
          productName: 'Paracetamol 500mg',
          productType: 'tablet',
          manufacturingDate: '2024-09-15',
          testDate: '2024-09-18',
          status: 'completed',
          testsPassed: 6,
          totalTests: 7,
          technician: 'Dr. Maria Konstantinou',
          blockchainHash: '0x7f9fade1c0d57a7af66ab4ead79fade1c0d57a7af66ab4ead7c2c2eb7b11a91385',
          complianceScore: 95
        },
        {
          id: 'BT-2024-001246',
          batchNumber: 'BT-2024-001246',
          productName: 'Ibuprofen 400mg',
          productType: 'tablet',
          manufacturingDate: '2024-09-14',
          testDate: '2024-09-17',
          status: 'failed',
          testsPassed: 4,
          totalTests: 6,
          technician: 'Dr. Nikos Papadopoulos',
          blockchainHash: '0x8a1bfde2d1e68b8bg77bc5fbe8afde2d1e68b8bg77bc5fbe8d3d3fc8c22ba2496',
          complianceScore: 67
        },
        {
          id: 'BT-2024-001245',
          batchNumber: 'BT-2024-001245',
          productName: 'Amoxicillin 250mg',
          productType: 'capsule',
          manufacturingDate: '2024-09-13',
          testDate: '2024-09-16',
          status: 'completed',
          testsPassed: 5,
          totalTests: 5,
          technician: 'Dr. Elena Georgiou',
          blockchainHash: '0x9b2cgef3e2f79c9ch88cd6gcf9bgef3e2f79c9ch88cd6gcf9e4e4gd9d33cb3507',
          complianceScore: 100
        },
        {
          id: 'BT-2024-001244',
          batchNumber: 'BT-2024-001244',
          productName: 'Aspirin 100mg',
          productType: 'tablet',
          manufacturingDate: '2024-09-12',
          testDate: '2024-09-15',
          status: 'pending',
          testsPassed: 3,
          totalTests: 6,
          technician: 'Dr. Dimitris Stavros',
          blockchainHash: null,
          complianceScore: null
        },
        {
          id: 'BT-2024-001243',
          batchNumber: 'BT-2024-001243',
          productName: 'Metformin 500mg',
          productType: 'tablet',
          manufacturingDate: '2024-09-11',
          testDate: '2024-09-14',
          status: 'completed',
          testsPassed: 7,
          totalTests: 7,
          technician: 'Dr. Sofia Alexandrou',
          blockchainHash: '0xac3dhfg4f3g80d0di99de7hdf0chfg4f3g80d0di99de7hdf0f5f5he0e44dc4618',
          complianceScore: 100
        }
      ];
      
      setBatchHistory(mockHistory);
      setFilteredHistory(mockHistory);
      setIsLoading(false);
    }, 1500);
  }, []);

  useEffect(() => {
    let filtered = [...batchHistory];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered?.filter(batch => 
        batch?.batchNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        batch?.productName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        batch?.technician?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered?.filter(batch => batch?.status === statusFilter);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.testDate) - new Date(a.testDate);
        case 'date_asc':
          return new Date(a.testDate) - new Date(b.testDate);
        case 'batch_number':
          return a?.batchNumber?.localeCompare(b?.batchNumber);
        case 'compliance_score':
          return (b?.complianceScore || 0) - (a?.complianceScore || 0);
        default:
          return 0;
      }
    });

    setFilteredHistory(filtered);
  }, [batchHistory, searchTerm, statusFilter, sortBy]);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle',
          label: 'Completed'
        };
      case 'failed':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          icon: 'XCircle',
          label: 'Failed'
        };
      case 'pending':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'Clock',
          label: 'Pending'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/10',
          icon: 'Circle',
          label: 'Unknown'
        };
    }
  };

  const getComplianceColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'pending', label: 'Pending' }
  ];

  const sortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'batch_number', label: 'Batch Number' },
    { value: 'compliance_score', label: 'Compliance Score' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="History" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Batch History</h3>
            <p className="text-sm text-muted-foreground">
              Previous testing records and compliance data
            </p>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Input
          type="search"
          placeholder="Search batches, products, or technicians..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="w-full"
        />

        <Select
          placeholder="Filter by status"
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
        />

        <Select
          placeholder="Sort by"
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
        />
      </div>
      {/* History List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)]?.map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="h-20 bg-muted rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : filteredHistory?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No batches found matching your criteria</p>
          </div>
        ) : (
          filteredHistory?.map((batch) => {
            const statusConfig = getStatusConfig(batch?.status);
            const isCurrentBatch = currentBatch?.batchNumber === batch?.batchNumber;
            
            return (
              <div
                key={batch?.id}
                className={`p-4 border rounded-lg transition-all duration-150 hover:shadow-sm cursor-pointer ${
                  isCurrentBatch 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/20'
                }`}
                onClick={() => onBatchSelect && onBatchSelect(batch)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`px-2 py-1 rounded-full ${statusConfig?.bgColor}`}>
                      <Icon name={statusConfig?.icon} size={12} className={statusConfig?.color} />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{batch?.batchNumber}</h4>
                      <p className="text-sm text-muted-foreground">{batch?.productName}</p>
                    </div>
                  </div>
                  
                  {batch?.complianceScore !== null && (
                    <div className="text-right">
                      <p className={`text-sm font-medium ${getComplianceColor(batch?.complianceScore)}`}>
                        {batch?.complianceScore}%
                      </p>
                      <p className="text-xs text-muted-foreground">Compliance</p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Test Date</p>
                    <p className="text-foreground">{formatDate(batch?.testDate)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tests Passed</p>
                    <p className="text-foreground">{batch?.testsPassed}/{batch?.totalTests}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Technician</p>
                    <p className="text-foreground truncate">{batch?.technician}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Blockchain</p>
                    <div className="flex items-center space-x-1">
                      {batch?.blockchainHash ? (
                        <>
                          <Icon name="CheckCircle" size={12} className="text-success" />
                          <span className="text-success text-xs">Verified</span>
                        </>
                      ) : (
                        <>
                          <Icon name="Clock" size={12} className="text-warning" />
                          <span className="text-warning text-xs">Pending</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {batch?.blockchainHash && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1">Blockchain Hash</p>
                    <p className="text-xs font-mono text-foreground bg-muted rounded px-2 py-1 truncate">
                      {batch?.blockchainHash}
                    </p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {/* Summary Stats */}
      {!isLoading && filteredHistory?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-foreground">
                {filteredHistory?.length}
              </p>
              <p className="text-sm text-muted-foreground">Total Batches</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-success">
                {filteredHistory?.filter(b => b?.status === 'completed')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-error">
                {filteredHistory?.filter(b => b?.status === 'failed')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Failed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-primary">
                {Math.round(
                  filteredHistory?.filter(b => b?.complianceScore !== null)?.reduce((acc, b) => acc + b?.complianceScore, 0) /
                  filteredHistory?.filter(b => b?.complianceScore !== null)?.length
                ) || 0}%
              </p>
              <p className="text-sm text-muted-foreground">Avg. Compliance</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchHistoryPanel;